import React, { forwardRef, useEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { useTypedDispatch, useTypedSelector } from "../../state/hooks";
import GQLProjects from "../../classes/api/GQL/projects/GQLprojects";
import { CheckMarkIconThin, IconFillStyles } from "../Iconset";
import ModalManager from "../layout/ModalManager";
import Dialog from "../layout/Dialog";
import StringProperty from "../properties/StringProperty";
import TextProperty from "../properties/TextProperty";
import NotificationPrompter from "../prompts/NotificationPrompter";
import StandbyOverlay from "../layout/StandbyOverlay";
import { openProjectFromLatestSave } from "../../classes/utilities/ProjectUtils";
import { IProjectInfo } from "../../interfaces/ScenarioElements";
import MessagePrompter from "../prompts/MessagePrompter";
import { SimulationStates } from "../../globals";
import { AppDispatch, StoreActions } from "../../state/store";

export default function RevisionSelector(props)
{
	const loadedScenarioId = useTypedSelector(state => state.scenarioModule.scenarioId);
	const revisions = useTypedSelector(state => state.scenarioModule.revisions);
	const currentRevision = useTypedSelector(state => state.scenarioModule.currentRevision);
	const dispatch = useTypedDispatch();
	const modalID = useRef<string>();
	const isViewOnlyMode = useTypedSelector<boolean>(state => state.applicationModule.isViewOnlyMode, shallowEqual);
	const isSaveDirty = useTypedSelector(state => state.historyModule.isSaveDirty, shallowEqual);
	const saveFileId = useTypedSelector(state => state.scenarioModule.projectSaveFileId);
	const cleanModal = () =>
	{
		if (modalID.current)
		{
			console.log("removing modal", modalID.current);
			ModalManager.removeModal(modalID.current);
			modalID.current = undefined;
		}
	}

	const revisionDropdown = () => 
	{
		return <div className="revisionSelectorListRoot">
			{
				revisions.map((revision, i) => (
					<div className="revisionSelectorListItem" key={i}
						onClick={() => 
						{
							if (currentRevision?.revisionNumber !== revision.revisionNumber)
							{
								console.log("clicked", revision);
								if (revision.projectId !== loadedScenarioId)
								{
									openProjectRevision(revision.projectId, dispatch, isViewOnlyMode, isSaveDirty);
								}
							}
							cleanModal();
						}}
					>
						{currentRevision?.revisionNumber === revision.revisionNumber ?
							<CheckMarkIconThin scale={1.5} fillStyle={IconFillStyles.white} />
							:
							<div className={"iconRenderContainer"} />
						}
						{revision.revisionName}
					</div>
				))
			}
			<hr />
			<div className="revisionSelectorListItem"
				onClick={() =>
				{
					console.log("clicked", currentRevision);
					if (loadedScenarioId)
					{
						ModalManager.addModal({
							content: <UpdateRevisionDialog 
								newRevision={true}
							/>,
							allowClickOut: false,
							showBackdrop: false,
						});
					}
					cleanModal();
				}}
			>
				<div className={"iconRenderContainer"} />New Revision</div>
			<div className="revisionSelectorListItem"
				
				onClick={() =>
				{
					console.log("clicked", currentRevision);
					if (loadedScenarioId)
					{
						ModalManager.addModal({
							content: <UpdateRevisionDialog 
								newRevision={false}
							/>,
							allowClickOut: false,
							showBackdrop: false,
						});
					}
					cleanModal();
				}}
			><div className={"iconRenderContainer"} />Edit Revision</div>
		</div>
	}

	useEffect(() => 
	{
		if (saveFileId)
		{
			GQLProjects.listProjectRevisions(loadedScenarioId).then(value => 
			{
				updateRevisions(value, dispatch);
			}).catch(error => 
			{
				console.error(error);
			});
		}
	}, [loadedScenarioId, saveFileId]);

	return <div className="revisionInfo currentRevision"
		onClick={(e) => 
		{
			if (revisions.length === 0)
			{
				return;
			}
			const rect = e.currentTarget.getBoundingClientRect();
			const modal = ModalManager.addModal({
				content: revisionDropdown(),
				allowClickOut: true,
				showBackdrop: false,
				rect: { x: rect?.left || 0, y: rect?.bottom || 0, width: rect?.width },
				onShutdown: () => { props.onShutdown?.(); },
			});
			modalID.current = modal;
		}}
	>{currentRevision?.revisionName}</div>;
}

export function UpdateRevisionDialog(props: {
	newRevision: boolean;
	onShutdown?: () => void;
})
{
	const dispatch = useTypedDispatch();
	const scenarioId = useTypedSelector(state => state.scenarioModule.scenarioId);
	const {revisionName, revisionNotes, revisionNumber} = useTypedSelector(state => state.scenarioModule.currentRevision);
	const highestRevisionNumber = useTypedSelector(state => state.scenarioModule.highestRevisionNumber);
	const projectSaveFileId = useTypedSelector(state => state.scenarioModule.projectSaveFileId);
	const [newRevisionName, setNewRevisionName] = useState(props.newRevision ? "v" + (highestRevisionNumber + 1) : revisionName);
	const [newRevisionNotes, setNewRevisionNotes] = useState(revisionNotes);

	const isViewOnlyMode = useTypedSelector<boolean>(state => state.applicationModule.isViewOnlyMode, shallowEqual);
	const isSaveDirty = useTypedSelector(state => state.historyModule.isSaveDirty, shallowEqual);


	const editRevisionDetails = () => 
	{
		GQLProjects.updateProjectRevision(scenarioId, {revisionName: newRevisionName, revisionNotes: newRevisionNotes}).then(value => 
		{
			updateRevisions(value, dispatch);
			NotificationPrompter.prompt({
				content: "Revision saved successfully",
				timeout: 7,
			});
		}).catch(error => 
		{
			NotificationPrompter.prompt({
				content: "Failed to save revision",
				timeout: 7,
			});
			console.error(error);
		});
	}

	const createRevision = () => 
	{
		GQLProjects.createProjectRevision(projectSaveFileId, {revisionName: newRevisionName, revisionNotes: newRevisionNotes}).then(value => 
		{
			openProjectRevision(value.id, dispatch, isViewOnlyMode, isSaveDirty);
			NotificationPrompter.prompt({
				content: "Revision created successfully",
				timeout: 7,
			});
		}).catch(error => 
		{
			NotificationPrompter.prompt({
				content: "Failed to create revision",
				timeout: 7,
			});
			console.error(error);
		});
	}

	useEffect(() => 
	{
		const interval = setInterval(() => 
		{
			console.log('activeElement check:', document.activeElement);
		}, 1000);

		return () => clearInterval(interval);
	}, []);

	return (
		<Dialog
			title={props.newRevision ? "New Revision" : "Edit Revision Details"}
			actions={[
				{label: "Save", onClick: () => { props.newRevision ? createRevision() : editRevisionDetails(); }},
				{label: "Cancel"}
			]}
			onShutdown={props.onShutdown}
			stopAutoFocus={true}
		>
			<StringProperty
				label="Revision Name"
				value={newRevisionName}
				autoFocus={true}
				onChangeCallback={(v: string) => { setNewRevisionName(v); }}
			/>
			<TextProperty
				label="Revision Notes"
				value={newRevisionNotes}
				onChangeCallback={(v: string) => { setNewRevisionNotes(v); }}
				placeholder={"Add notes"}
			/>
		</Dialog>
	)
}

function updateRevisions(value: {
	revisionNumber: number,
	revisionName: string,
	revisionNotes: string,
	id: string,
	revisions: {
		revisionNumber: number,
		revisionName: string,
		revisionNotes: string,
		currentSaveFile: {
			project: {
				id: string
			}
		}
	}[]
}, dispatch: AppDispatch) 
{
	dispatch(StoreActions.scenario.setRevisions(value.revisions.map(revision => ({
		revisionNumber: revision.revisionNumber,
		revisionName: revision.revisionName,
		revisionNotes: revision.revisionNotes,
		projectId: revision.currentSaveFile.project.id,
	}))));
	dispatch(StoreActions.scenario.setCurrentRevision({
		revisionNumber: value.revisionNumber,
		revisionName: value.revisionName,
		revisionNotes: value.revisionNotes,
		projectId: value.id,
	}));
}

function openProjectRevision(projectId: string, dispatch: AppDispatch, isViewOnlyMode: boolean, isSaveDirty: boolean)
{
	const fnOpenProject = () =>
	{
		StandbyOverlay.startStandbyMode();
		GQLProjects.getCmsProjectByID({
			id: projectId,
		}).then(value => 
		{
			StandbyOverlay.stopStandbyMode();
			openProjectFromLatestSave(value as IProjectInfo);
		}).catch(error => 
		{
			StandbyOverlay.stopStandbyMode();
			console.error(error);
		})
	}

	if(!isViewOnlyMode)
	{
		dispatch(StoreActions.application.setSimulationState(SimulationStates.Stopped));
	}

	if (isSaveDirty && !isViewOnlyMode)
	{
		const Warning_UnsavedChangesWillBeLost = {
			title: "Unsaved Changes",
			content:
					<div>
						Are you sure you want to close this Project?<br />
						Unsaved changes will be lost.
					</div>,
			actions: [
				{
					label: "Confirm",
					onClick: () => 
					{
						fnOpenProject();
					},
				},
				{
					label: "Cancel",
				},
			],
		};
		MessagePrompter.showWarning({
			...Warning_UnsavedChangesWillBeLost,
			actions: [
				{
					label: "Confirm",
					onClick: () =>
					{
						fnOpenProject();
					},
				},
				{
					label: "Cancel",
				},
			]
		});
	}
	else
	{
		fnOpenProject();
	}
}